import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import FarmerFormNew from "@/pages/FarmerFormNew";
import CreditReport from "@/pages/CreditReport";
import EntitlementReport from "@/pages/EntitlementReport";
import StartAssessmentFlow from "@/components/Assessment/StartAssessmentFlow";
import AssessmentWorkflow from "@/pages/AssessmentWorkflow";
import OfficerReview from "@/pages/OfficerReview";
import OfficerInbox from "@/pages/OfficerInbox";
import AgentDashboard from "@/pages/AgentDashboard";
import ManagementDashboard from "@/pages/ManagementDashboard";
import { Toaster } from "@/components/ui/sonner";

// Attach Authorization header on every axios request when a token exists.
// This ensures authenticated endpoints work even from components that do not
// pass headers explicitly.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const landingRouteForRole = (role) => {
  if (role === 'Field Agent') return '/agent/dashboard';
  if (role === 'Management') return '/management/dashboard';
  return '/officer/inbox';
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={landingRouteForRole(user.role)} />} />
          <Route path="/register" element={!user ? <Register onLogin={handleLogin} /> : <Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={
            user
              ? <Navigate to={landingRouteForRole(user.role)} replace />
              : <Navigate to="/login" />
          } />
          <Route path="/agent/dashboard" element={
            user
              ? (user.role === 'Field Agent'
                  ? <AgentDashboard user={user} onLogout={handleLogout} />
                  : <Navigate to={landingRouteForRole(user.role)} replace />)
              : <Navigate to="/login" />
          } />
          <Route path="/officer/inbox" element={
            user
              ? (user.role === 'Credit Officer' || user.role === 'System Administrator'
                  ? <OfficerInbox user={user} onLogout={handleLogout} />
                  : <Navigate to={landingRouteForRole(user.role)} replace />)
              : <Navigate to="/login" />
          } />
          <Route path="/management/dashboard" element={
            user
              ? (user.role === 'Management' || user.role === 'System Administrator'
                  ? <ManagementDashboard user={user} onLogout={handleLogout} />
                  : <Navigate to={landingRouteForRole(user.role)} replace />)
              : <Navigate to="/login" />
          } />
          <Route path="/farmers/new" element={user ? <FarmerFormNew user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/farmers/edit/:id" element={user ? <FarmerFormNew user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/farmers/:id/report" element={user ? <CreditReport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/entitlement-report/:farmerId" element={user ? <EntitlementReport user={user} /> : <Navigate to="/login" />} />
          <Route path="/assessment/new" element={user ? <StartAssessmentFlow user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/assessment/workflow/:journeyType" element={user ? <AssessmentWorkflow user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
          <Route path="/assessment/review/:assessmentId" element={
            user
              ? (user.role === 'Credit Officer' || user.role === 'System Administrator'
                  ? <OfficerReview user={user} />
                  : <Navigate to="/dashboard" replace />)
              : <Navigate to="/login" />
          } />
          <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;